// Data layer for the app -- reads/writes the relational tables in
// supabase/migrations/0002_relational_schema.sql (recipes, week_plans,
// plan_defaults, and their child tables), household-scoped per
// supabase/migrations/0004_household_scoping_schema.sql +
// 0005_household_scoping_lockdown.sql. Replaces the old generic
// get/set/delete/list `storage` interface (see git history for that
// version) now that the underlying data is real tables instead of one
// key/value blob per top-level piece of state.
//
// week_plans/plan_defaults load*/save* functions take `householdId` as their
// first argument -- reads filter by it, writes stamp every row with it. Row
// Level Security enforces the same boundary server-side (see the lockdown
// migration), so this is defense in depth, not the only thing standing
// between households' data -- but it's still required, since RLS can
// restrict which rows a write is *allowed* to touch, not invent the
// household_id value itself. Every save for these is a "replace child rows
// for what's in this save" operation (delete then reinsert) rather than a
// diff -- simplest correct approach at this app's scale, and safe
// specifically because useMealPlannerData.js's own mutation functions (e.g.
// deleteRecipe) already strip stale cross-references out of the JS state
// *before* any save fires, so a save's snapshot never references data that
// another concurrent save is in the middle of removing.
//
// Recipes are different: ownership shifted from household- to user-owned in
// the recipe-sharing rollout (supabase/migrations/0010-0012), so a loaded
// `recipes` array can contain rows owned by other users (via public
// visibility or an explicit share) that must never be touched by a bulk
// household-wide write. See the "recipes" section below -- it's per-recipe
// create/update/delete calls instead of a whole-collection load*/save* pair.
//
// Each load*/save* pair here hydrates the exact same JS shapes
// useMealPlannerData.js already worked with (see Schemas/*.md for the
// authoritative shape of each) from/to the relational rows.
import { supabase } from "./supabaseClient";
import { normalizeDishes } from "./planUtils";
import { DINING_OUT } from "./constants";

function groupBy(rows, key) {
  const out = {};
  rows.forEach((row) => {
    (out[row[key]] = out[row[key]] || []).push(row);
  });
  return out;
}

function chunk(array, size) {
  const out = [];
  for (let i = 0; i < array.length; i += size) out.push(array.slice(i, i + size));
  return out;
}

// A single upsert statement fails outright (Postgres: "ON CONFLICT DO
// UPDATE command cannot affect row a second time") if the same id appears
// more than once in one batch -- unlike a duplicate id across two separate
// calls, which upsert handles fine. This showed up in testing (React 18
// StrictMode deliberately double-invokes effects in dev, which surfaced a
// real path where the same recipe's data could end up queued for save
// twice over); rather than chase every possible source of a duplicate,
// de-duplicating right before every write makes the save correct
// regardless of how one got in -- last occurrence wins, on the assumption
// that whatever produced the duplicate had the later one as the more
// current value.
function dedupeById(rows) {
  const byId = new Map();
  rows.forEach((row) => byId.set(row.id, row));
  return Array.from(byId.values());
}

// A bulk import (hundreds of recipes at once) makes a single request per
// operation impractical -- both because the request body gets large, and
// because a stale-row delete filtered by "id not in (...)" would otherwise
// need every current id spelled out in the URL, which real proxies/servers
// reject once that list gets long. CHUNK_SIZE is conservative on purpose;
// there's no tight performance requirement here (imports are a rare,
// deliberate action, not a hot path).
const CHUNK_SIZE = 200;

// upsert, not insert: these batches are meant to fully replace what's
// there (the delete-then-reinsert pattern used throughout this file), but
// using upsert makes that replacement idempotent -- if a prior delete in
// the same save silently failed to clear a row (or another save for the
// same household raced in), reinserting the same id updates it in place
// instead of throwing a duplicate-key error and aborting the whole save
// partway through, which is what corrupted data during testing. Assumes
// `id`-keyed rows (recipes/recipe_ingredients/recipe_instructions, the
// only current callers) -- deduped for the reason in dedupeById's comment.
async function chunkedUpsert(table, rows) {
  const batches = chunk(dedupeById(rows), CHUNK_SIZE);
  for (let i = 0; i < batches.length; i++) {
    const { error } = await supabase.from(table).upsert(batches[i]);
    if (error) {
      console.error(`[db] chunkedUpsert ${table}: batch ${i + 1}/${batches.length} failed`, error, batches[i][0]);
      throw error;
    }
  }
}

// Plain insert, batched the same way as chunkedUpsert -- for callers where
// upsert's conflict-handling isn't actually needed (every row has a freshly
// generated id, so there's never a real id to conflict with) and, as it
// turns out, actively harmful: Postgres's `INSERT ... ON CONFLICT DO
// UPDATE` (what `.upsert()` compiles to) requires satisfying an UPDATE
// policy's authorization in addition to the INSERT policy's, even on rows
// that never actually conflict. On `recipes` -- whose INSERT and UPDATE
// policies use different expressions (unlike recipe_ingredients/
// recipe_instructions, which use one identical policy for every command)
// -- that combination 403'd every recipe import with "new row violates
// row-level security policy", confirmed by testing: an equivalent plain
// insert (the regular "+ New recipe" save path) worked, the upsert didn't.
async function chunkedInsert(table, rows) {
  const batches = chunk(dedupeById(rows), CHUNK_SIZE);
  for (let i = 0; i < batches.length; i++) {
    const { error } = await supabase.from(table).insert(batches[i]);
    if (error) {
      console.error(`[db] chunkedInsert ${table}: batch ${i + 1}/${batches.length} failed`, error, batches[i][0]);
      throw error;
    }
  }
}

// PostgREST caps how many rows a single SELECT returns (Supabase projects
// default this to 1000) and silently returns just that many rather than
// erroring -- fine for small tables, but a household with hundreds of
// recipes easily has thousands of recipe_ingredients/recipe_instructions
// rows. `build` returns a fresh query (select + filters, no .range() yet)
// each time it's called, since a query builder can only be awaited once.
//
// THE ORDER BY MUST BE A TOTAL ORDER (end with a unique column). Pages are
// LIMIT/OFFSET slices, and when the ORDER BY has ties -- e.g. `position`, which
// thousands of ingredient rows share -- Postgres may order the tied rows
// differently for each page's query, so the slices overlap and skip: some rows
// come back twice and others never. That silently dropped ~5% of the ingredient
// rows (and instructions) for an account that could see more than one page's
// worth, and saveRecipe then rewrites a recipe's stored ingredients from that
// incomplete in-memory copy. `uniqueKey` is a cheap safety net: if the result
// contains the same key twice the pages overlapped, so re-read, and only as a last resort de-duplicate.
async function selectAll(build, { uniqueKey } = {}) {
  let rows = await selectAllPages(build);
  if (!uniqueKey) return rows;
  const hasDuplicates = (list) => new Set(list.map((r) => r[uniqueKey])).size !== list.length;
  for (let retry = 0; retry < 2 && hasDuplicates(rows); retry++) {
    console.warn("[db] selectAll: overlapping pages (duplicate " + uniqueKey + "); re-reading");
    rows = await selectAllPages(build);
  }
  if (hasDuplicates(rows)) {
    console.error("[db] selectAll: pages still overlap after retries; de-duplicating by " + uniqueKey + " (some rows may be missing)");
    const byKey = new Map();
    rows.forEach((r) => byKey.set(r[uniqueKey], r));
    rows = Array.from(byKey.values());
  }
  return rows;
}

async function selectAllPages(build) {
  const PAGE_SIZE = 1000;
  let rows = [];
  let from = 0;
  for (;;) {
    const { data, error } = await build().range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    rows = rows.concat(data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return rows;
}

// Each save* below is a "delete this household's rows for this table, then
// reinsert" operation -- correct only if one save fully finishes before the
// next one for the same table starts. The debounced save effects in
// useMealPlannerData.js normally prevent overlap (each state change cancels
// the previous pending timer), but that only stops a timer that HASN'T
// fired yet -- it can't abort a save already in flight. Two rapid actions
// (e.g. clicking "Import" again before the first import's save has
// finished writing) can still start a second save while the first is
// mid-delete-and-reinsert, and the two interleave into duplicate-key errors
// or missing rows. `serialized` queues calls for the same key so each one
// waits for the previous to fully settle before starting, regardless of
// how often the underlying state changes.
const saveQueues = new Map();
function serialized(key, fn) {
  const previous = saveQueues.get(key) || Promise.resolve();
  const next = previous.then(fn, fn);
  // Swallow rejections in the queue chain itself (not in what the caller
  // receives) so one failed save doesn't permanently wedge future saves --
  // the caller of `next` still sees the real rejection via `next` itself.
  saveQueues.set(key, next.catch(() => {}));
  return next;
}

// ---------------------------------------------------------------- recipes ---
// Recipes moved from household-owned to user-owned in the recipe-sharing
// rollout (see supabase/migrations/0010-0012) -- there's no household_id on
// these three tables anymore. Visibility (own + public + shared-with-me) is
// entirely RLS's job now, so loadRecipes takes no argument and just reads
// whatever the signed-in user is allowed to see. Because the resulting
// `recipes` array can contain rows owned by OTHER users, writes are no
// longer a single "replace this household's whole collection" operation
// (that would delete/reinsert data belonging to people other than the
// caller) -- each mutation below is a targeted, single-recipe call instead.
function recipeRowFields(r) {
  // active_time/cook_time/servings are integer columns -- servings math
  // already rounds everywhere else in the app, and recipe times are
  // essentially always whole minutes, so a fractional value here (from an
  // import file, or hand-typed into RecipeForm's plain number inputs, which
  // don't enforce whole numbers) gets rounded rather than failing the save
  // with "invalid input syntax for type integer".
  const int = (v, fallback) => (v == null || isNaN(Number(v)) ? fallback : Math.round(Number(v)));
  // calories/protein/carbs/fat per serving are `numeric` columns -- real
  // nutrition data legitimately has decimals (12.5g protein), so these are
  // only coerced to a number, never rounded.
  const num = (v, fallback) => (v == null || isNaN(Number(v)) ? fallback : Number(v));
  return {
    title: r.title || "",
    cuisine: r.cuisine || "",
    source: r.source || "",
    // The source's web address, kept apart from its name (0056).
    source_url: r.sourceUrl || null,
    favorite: !!r.favorite,
    meal_types: r.mealTypes || [],
    recipe_type: r.recipeType || "entree",
    diet_tags: r.dietTags || [],
    seasons: r.seasons || [],
    cooking_method: r.cookingMethod || [],
    active_time: int(r.activeTime, 15),
    cook_time: int(r.cookTime, 15),
    servings: int(r.servings, 4),
    yield: r.yield || "",
    calories_per_serving: num(r.caloriesPerServing, 0),
    protein_per_serving: num(r.proteinPerServing, 0),
    carbs_per_serving: num(r.carbsPerServing, 0),
    fat_per_serving: num(r.fatPerServing, 0),
    is_public: !!r.isPublic,
    editable_by_others: !!r.editableByOthers,
    updated_at: new Date().toISOString(),
  };
}

// Replaces ONE recipe's ingredients/instructions -- scoped by recipe_id,
// never by household (there is no household scope for these tables anymore,
// and even if there were, a whole-collection sweep would risk touching rows
// belonging to other users visible in the same loaded `recipes` array).
async function replaceRecipeChildren(recipe) {
  const { error: deleteIngredientsError } = await supabase.from("recipe_ingredients").delete().eq("recipe_id", recipe.id);
  if (deleteIngredientsError) throw deleteIngredientsError;
  const { error: deleteInstructionsError } = await supabase.from("recipe_instructions").delete().eq("recipe_id", recipe.id);
  if (deleteInstructionsError) throw deleteInstructionsError;

  const ingredientRows = (recipe.ingredients || []).map((ing, i) => ({
    id: ing.id,
    recipe_id: recipe.id,
    position: i,
    component: ing.component || "",
    type: ing.type === "note" ? "note" : "ingredient",
    name: ing.name || "",
    prep: ing.prep || "",
    quantity: ing.quantity ?? "",
    unit: ing.unit || "",
    category: ing.category || "other",
  }));
  const instructionRows = (recipe.instructions || []).map((ins, i) => ({
    id: ins.id,
    recipe_id: recipe.id,
    position: i,
    component: ins.component || "",
    type: ins.type === "note" ? "note" : "step",
    text: ins.text || "",
  }));

  await chunkedUpsert("recipe_ingredients", ingredientRows);
  await chunkedUpsert("recipe_instructions", instructionRows);
}

export async function loadRecipes() {
  let recipeRows;
  try {
    recipeRows = await selectAll(() => supabase.from("recipes").select("*").order("created_at", { ascending: true }).order("id", { ascending: true }), { uniqueKey: "id" });
  } catch {
    return [];
  }

  // recipe_id, position, then id: grouped by recipe in display order, and a
  // total order (`position` alone is not -- see selectAll).
  const ingredientRows = await selectAll(() => supabase.from("recipe_ingredients").select("*").order("recipe_id", { ascending: true }).order("position", { ascending: true }).order("id", { ascending: true }), { uniqueKey: "id" });
  const instructionRows = await selectAll(() => supabase.from("recipe_instructions").select("*").order("recipe_id", { ascending: true }).order("position", { ascending: true }).order("id", { ascending: true }), { uniqueKey: "id" });
  const ingredientsByRecipe = groupBy(ingredientRows || [], "recipe_id");
  const instructionsByRecipe = groupBy(instructionRows || [], "recipe_id");

  return recipeRows.map((r) => ({
    id: r.id,
    title: r.title,
    cuisine: r.cuisine,
    source: r.source,
    sourceUrl: r.source_url || "",
    favorite: r.favorite,
    mealTypes: r.meal_types || [],
    recipeType: r.recipe_type,
    dietTags: r.diet_tags || [],
    seasons: r.seasons || [],
    cookingMethod: r.cooking_method || [],
    activeTime: r.active_time,
    cookTime: r.cook_time,
    servings: r.servings,
    yield: r.yield,
    // These four are `numeric` columns -- PostgREST serializes numeric as a
    // JSON string (to avoid float precision loss), so they need an explicit
    // Number(...) here to come back as the plain JS numbers the rest of the
    // app expects, same as recipe-schema.md documents.
    caloriesPerServing: Number(r.calories_per_serving) || 0,
    proteinPerServing: Number(r.protein_per_serving) || 0,
    carbsPerServing: Number(r.carbs_per_serving) || 0,
    fatPerServing: Number(r.fat_per_serving) || 0,
    ownerUserId: r.owner_user_id,
    createdAt: r.created_at,
    isPublic: !!r.is_public,
    editableByOthers: !!r.editable_by_others,
    ingredients: (ingredientsByRecipe[r.id] || []).map((ing) => ({
      id: ing.id,
      component: ing.component,
      type: ing.type || "ingredient",
      name: ing.name,
      prep: ing.prep,
      quantity: ing.quantity,
      unit: ing.unit,
      category: ing.category,
    })),
    instructions: (instructionsByRecipe[r.id] || []).map((ins) => ({
      id: ins.id,
      component: ins.component,
      type: ins.type,
      text: ins.text,
    })),
  }));
}

// The set of recipe ids explicitly shared with the signed-in user -- needed
// because `select *` under RLS returns own + public + shared-with-me all
// mixed together, and the client needs to know *why* a given row is
// visible (e.g. to compute whether it's currently editable).
// Recipes shared directly with the signed-in user, as a Map of recipe id ->
// when it was shared (ISO). Callers mostly ask .has(id); the dates feed the
// library's "Recently shared with you" (redesign slice 7a). A recipe shared
// more than once keeps its newest share.
export async function loadSharedWithMeRecipeIds(userId) {
  const { data, error } = await supabase.from("recipe_shares").select("recipe_id, created_at").eq("shared_with_user_id", userId);
  if (error) throw error;
  const out = new Map();
  (data || []).forEach((row) => {
    const at = new Date(row.created_at).toISOString();
    if (!out.has(row.recipe_id) || out.get(row.recipe_id) < at) out.set(row.recipe_id, at);
  });
  return out;
}

// The set of the signed-in user's OWN recipes that currently have at least
// one active share -- this is what drives the editableByOthers lock (see
// the recipes_guard_immutable_fields trigger) from the owner's side.
// Membership alone is all the UI needs (lock/unlock); the exact grants for
// a given recipe are fetched separately via listRecipeShares when the
// owner opens that recipe's share panel.
export async function loadMySharedRecipeIds(userId) {
  const { data, error } = await supabase.from("recipe_shares").select("recipe_id").eq("shared_by_user_id", userId);
  if (error) throw error;
  return new Set((data || []).map((row) => row.recipe_id));
}

export async function createRecipe(recipe, ownerUserId) {
  const row = { id: recipe.id, owner_user_id: ownerUserId, ...recipeRowFields(recipe) };
  const { error } = await supabase.from("recipes").insert(row);
  if (error) throw error;
  await replaceRecipeChildren(recipe);
}

// A plain UPDATE, not an upsert -- mixing a split INSERT/UPDATE RLS policy
// pair with ON CONFLICT's insert-then-resolve path is a known sharp edge.
// owner_user_id is deliberately never included in the payload (it's
// immutable -- see the recipes_guard_immutable_fields trigger).
export async function updateRecipe(recipe) {
  const { error } = await supabase.from("recipes").update(recipeRowFields(recipe)).eq("id", recipe.id);
  if (error) throw error;
  await replaceRecipeChildren(recipe);
}

export async function deleteRecipe(id) {
  const { error } = await supabase.from("recipes").delete().eq("id", id);
  if (error) throw error;
}

// The actual delete path used by the app now -- see leave_or_delete_recipe
// in supabase/migrations/0032_leave_or_delete_recipe.sql for what "leave or
// delete" resolves to depending on caller/recipe state (plain delete,
// ownership transfer to another holder, or removing just your own share).
// deleteRecipe above is kept as a plain, unused-but-intact table delete --
// nothing else calls it, so there was no reason to remove it.
export async function leaveOrDeleteRecipe(recipeId) {
  const { error } = await supabase.rpc("leave_or_delete_recipe", { target_recipe_id: recipeId });
  if (error) throw error;
}

// Bulk-create for imports, reusing chunkedUpsert (id-keyed, safe as an
// upsert since these are all brand-new ids). isPublic/editableByOthers are
// forced false here regardless of what an imported JSON file contains -- a
// bulk import must not be able to make recipes public or eternally
// shared-editable via a data field, only through the explicit per-recipe UI
// checkbox at creation.
export async function importRecipes(recipes, ownerUserId) {
  const recipeRows = recipes.map((r) => ({
    id: r.id,
    owner_user_id: ownerUserId,
    ...recipeRowFields(r),
    is_public: false,
    editable_by_others: false,
  }));
  // Insert, not upsert -- see chunkedInsert's comment for why upsert
  // actively fails here under RLS, and why a plain insert is correct
  // anyway: every id here is a freshly generated uid(), never a real
  // pre-existing recipe.
  await chunkedInsert("recipes", recipeRows);

  const ingredientRows = [];
  const instructionRows = [];
  recipes.forEach((r) => {
    (r.ingredients || []).forEach((ing, i) => {
      ingredientRows.push({
        id: ing.id,
        recipe_id: r.id,
        position: i,
        component: ing.component || "",
        type: ing.type === "note" ? "note" : "ingredient",
        name: ing.name || "",
        prep: ing.prep || "",
        quantity: ing.quantity ?? "",
        unit: ing.unit || "",
        category: ing.category || "other",
      });
    });
    (r.instructions || []).forEach((ins, i) => {
      instructionRows.push({
        id: ins.id,
        recipe_id: r.id,
        position: i,
        component: ins.component || "",
        type: ins.type === "note" ? "note" : "step",
        text: ins.text || "",
      });
    });
  });

  await chunkedUpsert("recipe_ingredients", ingredientRows);
  await chunkedUpsert("recipe_instructions", instructionRows);
}

// Asks the import-recipe-from-url edge function to fetch a page and parse
// its schema.org Recipe JSON-LD -- returns { recipe, dedupRecipeId,
// normalizedUrl }. `recipe` is a loosely-shaped object, not yet inserted
// anywhere -- callers should run it through normalizeImportedRecipe
// (src/lib/recipeUtils.js) before treating it as a real recipe, same as the
// file-import path. `dedupRecipeId` is non-null when this exact URL was
// already imported by someone else -- see registerRecipeUrlImport/
// claimRecipeUrlImport below and supabase/migrations/0029.
export async function importRecipeFromUrl(url) {
  const { data, error } = await supabase.functions.invoke("import-recipe-from-url", { body: { url } });
  if (error) {
    // supabase-js reports a generic "non-2xx status code" message for any
    // failed invocation and leaves `data` empty -- the actual error code/
    // message our function sent back lives in the response body, only
    // reachable via error.context (the raw Response).
    let message = error.message;
    if (error.context && typeof error.context.json === "function") {
      try {
        const body = await error.context.json();
        message = body?.message || body?.error || message;
      } catch {
        // Response body wasn't JSON (e.g. a network-level failure before
        // our function ran) -- fall back to the SDK's generic message.
      }
    }
    throw new Error(message);
  }
  if (data?.error) throw new Error(data.message || data.error);
  return data;
}

// Registers `normalizedUrl` (as computed by the edge function -- clients
// never compute this themselves, so there's only ever one normalization
// implementation to keep correct) as the canonical import for `recipeId`,
// so a later importer of the exact same URL gets linked to this recipe
// instead of creating a duplicate. Best-effort: a lost race against another
// insert (23505, unique violation on normalized_url) just means someone
// else's import already claimed this URL a moment earlier -- the recipe
// this caller just created is unaffected either way.
export async function registerRecipeUrlImport(normalizedUrl, recipeId) {
  const { error } = await supabase.from("recipe_url_imports").insert({ normalized_url: normalizedUrl, recipe_id: recipeId });
  if (error && error.code !== "23505") throw error;
}

// Grants the signed-in user view access (via recipe_shares) to a recipe
// that's registered as a URL-import's canonical target -- see
// claim_recipe_url_import in supabase/migrations/0029, which does the
// actual insert as a security-definer function since the caller isn't the
// recipe's owner and couldn't insert into recipe_shares directly.
export async function claimRecipeUrlImport(recipeId) {
  const { error } = await supabase.rpc("claim_recipe_url_import", { target_recipe_id: recipeId });
  if (error) throw error;
}

// favorite is deliberately excluded from the recipes_guard_immutable_fields
// content freeze (see 0010_recipe_ownership.sql) -- it's a personal marker,
// not recipe content, so this stays available to the owner even on a
// public/locked-shared recipe. Restricted to the owner in the UI because
// it's a single column on the shared row -- a non-owner flipping it would
// change what every other viewer of the same public/shared recipe sees.
// See recipe_favorites below for how a non-owner favorites the same
// recipe without that problem.
export async function toggleRecipeFavorite(id, value) {
  const { error } = await supabase.from("recipes").update({ favorite: value }).eq("id", id);
  if (error) throw error;
}

// ----------------------------------------------------- recipe favorites ---
// Per-user favorite marker (supabase/migrations/0016) for a recipe someone
// doesn't own -- a public or shared-with-me recipe. Deliberately a
// separate mechanism from the `recipes.favorite` column above rather than
// a replacement for it: an owner's own favorite has exactly one viewer
// (themselves) so the shared column was never actually wrong for that
// case, only for everyone else looking at the same row.
export async function loadMyFavoriteRecipeIds(userId) {
  const { data, error } = await supabase.from("recipe_favorites").select("recipe_id").eq("user_id", userId);
  if (error) throw error;
  return new Set((data || []).map((row) => row.recipe_id));
}

export async function setMyRecipeFavorite(recipeId, value) {
  if (value) {
    const { error } = await supabase.from("recipe_favorites").upsert({ recipe_id: recipeId });
    if (error) throw error;
  } else {
    const { error } = await supabase.from("recipe_favorites").delete().eq("recipe_id", recipeId);
    if (error) throw error;
  }
}

// If this is the recipe's first share, editableByOthers is still mutable
// (it locks the moment a share exists) -- persist whatever was just
// confirmed in the Share panel before creating the grant. On a second+
// share, editableByOthers is already locked, so the caller should pass
// isFirstShare: false and this update is skipped.
export async function shareRecipe(recipeId, email, editableByOthers, isFirstShare) {
  if (isFirstShare) {
    const { error: lockError } = await supabase.from("recipes").update({ editable_by_others: !!editableByOthers }).eq("id", recipeId);
    if (lockError) throw lockError;
  }
  const { data: sharedWithUserId, error: lookupError } = await supabase.rpc("find_user_id_by_email", { target_email: email });
  if (lookupError) throw lookupError;
  if (!sharedWithUserId) throw new Error(`No account found for ${email}`);
  const { error } = await supabase.from("recipe_shares").insert({ recipe_id: recipeId, shared_with_user_id: sharedWithUserId });
  if (error) {
    // Postgres unique_violation -- the (recipe_id, shared_with_user_id)
    // constraint on recipe_shares (0011_recipe_shares.sql) already blocks a
    // duplicate share; surface that as a plain sentence instead of the raw
    // constraint-violation message.
    if (error.code === "23505") throw new Error("This recipe has already been shared with that person.");
    throw error;
  }
}

// The current user's own "who shared this with me" fact, and only that --
// see get_recipe_shared_by's comment in
// 0013_recipe_share_links_and_shared_by.sql for why it's scoped that way.
// Returns { email, origin } -- origin is "explicit" (owner deliberately
// shared) or "url_import" (both households independently imported the
// same URL, see 0033_recipe_shares_origin.sql) -- or null when the recipe
// wasn't shared with this user (e.g. they own it, or it's just public).
export async function getRecipeSharedBy(recipeId) {
  const { data, error } = await supabase.rpc("get_recipe_shared_by", { target_recipe_id: recipeId });
  if (error) throw error;
  return data?.[0] || null;
}

// Turns a recipe public or private (the share sheet, slice 8a). Only that
// one column: a whole-recipe save would rewrite its ingredients, which a
// public recipe doesn't allow. The owner may always change it (the recipe
// guard, 0011/0032/0056, lets is_public through for the owner).
export async function setRecipePublic(recipeId, isPublic) {
  const { error } = await supabase.from("recipes").update({ is_public: !!isPublic, updated_at: new Date().toISOString() }).eq("id", recipeId);
  if (error) throw error;
}

export async function revokeRecipeShare(recipeId, sharedWithUserId) {
  const { error } = await supabase.from("recipe_shares").delete().eq("recipe_id", recipeId).eq("shared_with_user_id", sharedWithUserId);
  if (error) throw error;
}

export async function listRecipeShares(recipeId) {
  const { data, error } = await supabase.rpc("list_recipe_shares_with_email", { target_recipe_id: recipeId });
  if (error) throw error;
  return data || [];
}

// --------------------------------------------------------- share links ---
// A link is a separate mechanism from the by-email shares above -- see
// 0013_recipe_share_links_and_shared_by.sql's comment -- for sending a
// recipe to someone with no account (email/text), rather than granting an
// existing account persistent access.
export async function listRecipeShareLinks(recipeId) {
  const { data, error } = await supabase.from("recipe_share_links").select("id, token, created_at").eq("recipe_id", recipeId).order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createRecipeShareLink(recipeId) {
  const { data, error } = await supabase.from("recipe_share_links").insert({ recipe_id: recipeId }).select("id, token, created_at").single();
  if (error) throw error;
  return data;
}

export async function revokeRecipeShareLink(linkId) {
  const { error } = await supabase.from("recipe_share_links").delete().eq("id", linkId);
  if (error) throw error;
}

// The one call a signed-out visitor makes -- see get_recipe_by_share_token's
// comment for why this is safe to expose to `anon`. Returns null for an
// invalid/revoked token rather than throwing, so the caller can render a
// plain "link not found" state.
export async function getRecipeByShareToken(token) {
  const { data, error } = await supabase.rpc("get_recipe_by_share_token", { target_token: token });
  if (error) throw error;
  return data || null;
}

// ------------------------------------------------------------- week plans ---
// week_plan_meals' source columns (0053) <-> a leftovers placement's link
// to the meal it draws on (planScaling.ts's LeftoverSource).
function sourceFromRow(m) {
  if (!m.source_week_start) return null;
  return { weekStart: m.source_week_start, day: m.source_day, mealSlot: m.source_meal_slot, dishType: m.source_dish_type, scalesSource: m.scales_source !== false };
}

function sourceColumns(source) {
  return {
    source_week_start: source?.weekStart ?? null,
    source_day: source?.day ?? null,
    source_meal_slot: source?.mealSlot ?? null,
    source_dish_type: source?.dishType ?? null,
    scales_source: source ? source.scalesSource !== false : true,
  };
}

export async function loadWeekPlans(householdId) {
  let weekRows;
  try {
    weekRows = await selectAll(() => supabase.from("week_plans").select("*").eq("household_id", householdId).order("week_start", { ascending: true }), { uniqueKey: "week_start" });
  } catch {
    return {};
  }

  // The primary key, so the order is total: (household_id, week_start, day, meal_slot, dish_type).
  const mealRows = await selectAll(() => supabase.from("week_plan_meals").select("*").eq("household_id", householdId).order("week_start", { ascending: true }).order("day", { ascending: true }).order("meal_slot", { ascending: true }).order("dish_type", { ascending: true }));
  const itemRows = await selectAll(() => supabase.from("week_plan_items").select("*").eq("household_id", householdId).order("id", { ascending: true }), { uniqueKey: "id" });

  const result = {};
  weekRows.forEach((w) => {
    result[w.week_start] = {
      title: w.title || "",
      days: {},
      snacks: [],
      preserves: [],
      medicinal: [],
      servings: w.servings,
      dayBudgets: w.day_budgets || {},
      leftovers: {},
      leftoverSources: {},
      // Same keys: when a dish was marked cooked (0058), ISO.
      cooked: {},
    };
  });

  (mealRows || []).forEach((m) => {
    const wd = result[m.week_start];
    if (!wd) return;
    const key = `${m.day}|${m.meal_slot}|${m.dish_type}`;
    wd.days[m.day] = wd.days[m.day] || {};
    wd.days[m.day][m.meal_slot] = wd.days[m.day][m.meal_slot] || {};
    wd.days[m.day][m.meal_slot][m.dish_type] = m.is_dining_out ? DINING_OUT : m.recipe_id || "";
    if (m.is_leftover) wd.leftovers[key] = true;
    const source = sourceFromRow(m);
    if (m.is_leftover && source) wd.leftoverSources[key] = source;
    if (m.cooked_at) wd.cooked[key] = new Date(m.cooked_at).toISOString();
  });

  // {id, recipeId} per entry, not a bare recipe id -- removing one entry
  // has to target its own database row (recipe_id alone isn't unique; the
  // same recipe can legitimately repeat in a list), and per-row targeting
  // is what makes removeWeekPlanItem below safe under concurrent editing.
  (itemRows || []).forEach((it) => {
    const wd = result[it.week_start];
    if (!wd) return;
    const field = it.list_type === "snack" ? "snacks" : it.list_type;
    wd[field].push({ id: it.id, recipeId: it.recipe_id });
  });

  return result;
}

// --------------------------------------------------- week plan cells ---
// Piece B of the collaborative-editing follow-up: replaces the old
// saveWeekPlans/saveWeekPlansInner (deleted every week_plan_meals/
// week_plan_items row for every week in memory, then reinserted
// everything, on every debounced tick) with one targeted call per user
// action. That approach was safe only because a single household's own
// browser session was ever the sole writer for its own data; the moment a
// second household can also write to the same week (household-to-household
// sharing, see 0018_week_plan_sharing_schema.sql), two independent
// sessions each doing "read current state, delete everything, reinsert
// everything" around the same time can silently drop each other's writes.
// A per-cell upsert/delete never has that failure mode -- each call only
// ever touches the exact row it's about.
//
// week_plan_meals/week_plan_items both have a composite FK back to
// week_plans(household_id, week_start) (0005_household_scoping_lockdown.sql)
// -- the parent row has to exist before the first cell write for a brand
// new week. ensureWeekPlanRow upserts it with ignoreDuplicates so it never
// clobbers an existing row's title/servings/dayBudgets, and ON CONFLICT DO
// NOTHING means it only ever needs the insert policy to be satisfied, not
// update too (see chunkedInsert's comment above for why that combination
// matters under RLS).
async function ensureWeekPlanRow(householdId, weekStart, defaults) {
  const { error } = await supabase.from("week_plans").upsert(
    { household_id: householdId, week_start: weekStart, title: defaults?.title || "", servings: defaults?.servings || 4, day_budgets: defaults?.dayBudgets || {} },
    { onConflict: "household_id,week_start", ignoreDuplicates: true }
  );
  if (error) throw error;
}

// Title/servings/dayBudgets only -- meals/items are saved separately,
// below, since they no longer travel together as one whole-object save.
export async function saveWeekMeta(householdId, weekStart, { title, servings, dayBudgets }) {
  const { error } = await supabase.from("week_plans").upsert({
    household_id: householdId,
    week_start: weekStart,
    title: title || "",
    servings: servings || 4,
    day_budgets: dayBudgets || {},
    updated_at: new Date().toISOString(),
  });
  if (error) throw error;
}

// `source` links a leftovers placement to the meal it draws on; null for a
// cooked dish or "Something older" leftovers. The database drops a link that
// doesn't fit (see 0053's week_plan_meals_normalize_source).
export async function saveWeekPlanMeal(householdId, weekStart, day, mealSlot, dishType, recipeId, isLeftover, defaults, source = null) {
  await ensureWeekPlanRow(householdId, weekStart, defaults);
  const isDiningOut = recipeId === DINING_OUT;
  const { error } = await supabase.from("week_plan_meals").upsert({
    household_id: householdId,
    week_start: weekStart,
    day,
    meal_slot: mealSlot,
    dish_type: dishType,
    recipe_id: isDiningOut ? null : recipeId || null,
    is_leftover: !!isLeftover,
    is_dining_out: isDiningOut,
    ...sourceColumns(isLeftover ? source : null),
  });
  if (error) throw error;
}

// For removing a non-entree dish-type row entirely (an entree row instead
// just gets upserted back to recipe_id: null via saveWeekPlanMeal, since
// there's always exactly one entree slot per meal -- see PlanPage.jsx's
// removeDishRow).
export async function deleteWeekPlanMealRow(householdId, weekStart, day, mealSlot, dishType) {
  const { error } = await supabase
    .from("week_plan_meals")
    .delete()
    .eq("household_id", householdId)
    .eq("week_start", weekStart)
    .eq("day", day)
    .eq("meal_slot", mealSlot)
    .eq("dish_type", dishType);
  if (error) throw error;
}

// Saves a set of placement changes (planMoves.ts's placementChanges), e.g.
// a meal moved to another day. Three passes, because of 0053's triggers: a
// link is only kept when its source row already holds the same recipe, and
// a source row's recipe change rewrites the leftovers drawing on it.
//   1. write every changed row without its link;
//   2. delete the rows that are gone;
//   3. write the leftovers rows that have a link, now that their sources
//      are in place.
// `defaults` creates the week_plans row for `weekStart` if it's missing.
export async function savePlacementChanges(householdId, weekStart, { upserts, deletes }, defaults) {
  if (upserts.length === 0 && deletes.length === 0) return;
  await ensureWeekPlanRow(householdId, weekStart, defaults);
  const row = (r, source) => {
    const isDiningOut = r.recipeId === DINING_OUT;
    return {
      household_id: householdId,
      week_start: r.weekStart,
      day: r.day,
      meal_slot: r.mealSlot,
      dish_type: r.dishType,
      recipe_id: isDiningOut ? null : r.recipeId || null,
      is_leftover: !!r.isLeftover,
      is_dining_out: isDiningOut,
      cooked_at: r.cookedAt ?? null,
      ...sourceColumns(source),
    };
  };
  if (upserts.length > 0) {
    const { error } = await supabase.from("week_plan_meals").upsert(upserts.map((r) => row(r, null)));
    if (error) throw error;
  }
  for (const d of deletes) await deleteWeekPlanMealRow(householdId, d.weekStart, d.day, d.mealSlot, d.dishType);
  const linked = upserts.filter((r) => r.isLeftover && r.source);
  if (linked.length > 0) {
    const { error } = await supabase.from("week_plan_meals").upsert(linked.map((r) => row(r, r.source)));
    if (error) throw error;
  }
}

// Marks a planned dish cooked (an ISO time) or not (null) -- slice 7c, 0058.
// Only the mark changes; the database drops it again if the dish changes.
export async function setWeekPlanMealCooked(householdId, weekStart, day, mealSlot, dishType, cookedAt) {
  const { error } = await supabase
    .from("week_plan_meals")
    .update({ cooked_at: cookedAt })
    .eq("household_id", householdId)
    .eq("week_start", weekStart)
    .eq("day", day)
    .eq("meal_slot", mealSlot)
    .eq("dish_type", dishType);
  if (error) throw error;
}

// Returns the new row's real id -- callers need it to store {id, recipeId}
// in local state so a later removeWeekPlanItem can target this exact row.
export async function addWeekPlanItem(householdId, weekStart, listType, recipeId, defaults) {
  await ensureWeekPlanRow(householdId, weekStart, defaults);
  const { data, error } = await supabase
    .from("week_plan_items")
    .insert({ household_id: householdId, week_start: weekStart, list_type: listType, recipe_id: recipeId })
    .select("id")
    .single();
  if (error) throw error;
  return data.id;
}

export async function removeWeekPlanItem(itemId) {
  const { error } = await supabase.from("week_plan_items").delete().eq("id", itemId);
  if (error) throw error;
}

// The only sanctioned way to delete more than one week_plan_meals/
// week_plan_items row in a single action -- see clear_week_plan_day/
// clear_week_plan_week's comment in 0019_week_plan_visibility_lockdown.sql
// for why this has to be a security-definer RPC with an explicit ownership
// check, rather than something expressible as a DELETE policy: a
// single-row delete (an ordinary edit a collaborator legitimately needs)
// and a many-row delete (these) are the same statement shape to RLS.
export async function clearWeekPlanDay(householdId, weekStart, day) {
  const { error } = await supabase.rpc("clear_week_plan_day", { target_household_id: householdId, target_week_start: weekStart, target_day: day });
  if (error) throw error;
}

export async function clearWeekPlanWeek(householdId, weekStart) {
  const { error } = await supabase.rpc("clear_week_plan_week", { target_household_id: householdId, target_week_start: weekStart });
  if (error) throw error;
}

// The one remaining legitimate bulk-replace -- "Use as template" wholesale
// overwrites the current week's meals/items from a past week. Unlike the
// old saveWeekPlans, this is scoped to exactly one week (never every week
// in memory) and is only ever invoked by a household replacing its OWN
// current week from its OWN past data -- never reachable for a foreign
// household's plan -- so delete-then-reinsert is still the right shape
// here, just properly scoped. Returns the newly-inserted item rows (with
// their real ids) so the caller can update local state's {id, recipeId}
// entries -- template-copying always creates brand new rows, never reuses
// the source week's ids.
export async function replaceWeekPlanWeek(householdId, weekStart, weekData) {
  await saveWeekMeta(householdId, weekStart, weekData);

  const { error: deleteMealsError } = await supabase.from("week_plan_meals").delete().eq("household_id", householdId).eq("week_start", weekStart);
  if (deleteMealsError) throw deleteMealsError;
  const { error: deleteItemsError } = await supabase.from("week_plan_items").delete().eq("household_id", householdId).eq("week_start", weekStart);
  if (deleteItemsError) throw deleteItemsError;

  const mealRows = [];
  Object.entries(weekData.days || {}).forEach(([day, slots]) => {
    Object.entries(slots || {}).forEach(([mealSlot, dishes]) => {
      Object.entries(normalizeDishes(dishes)).forEach(([dishType, recipeId]) => {
        const isDiningOut = recipeId === DINING_OUT;
        mealRows.push({
          household_id: householdId,
          week_start: weekStart,
          day,
          meal_slot: mealSlot,
          dish_type: dishType,
          recipe_id: isDiningOut ? null : recipeId || null,
          is_leftover: !!weekData.leftovers?.[`${day}|${mealSlot}|${dishType}`],
          is_dining_out: isDiningOut,
        });
      });
    });
  });
  if (mealRows.length > 0) {
    const { error } = await supabase.from("week_plan_meals").upsert(mealRows);
    if (error) throw error;
  }
  // Links second: a source may be one of the rows just written.
  const linked = mealRows
    .map((row) => ({ row, source: row.is_leftover ? weekData.leftoverSources?.[`${row.day}|${row.meal_slot}|${row.dish_type}`] : null }))
    .filter(({ source }) => source)
    .map(({ row, source }) => ({ ...row, ...sourceColumns(source) }));
  if (linked.length > 0) {
    const { error } = await supabase.from("week_plan_meals").upsert(linked);
    if (error) throw error;
  }

  const itemRows = [];
  [
    ["snacks", "snack"],
    ["preserves", "preserves"],
    ["medicinal", "medicinal"],
  ].forEach(([field, listType]) => {
    (weekData[field] || []).forEach((entry) => {
      itemRows.push({ household_id: householdId, week_start: weekStart, list_type: listType, recipe_id: entry.recipeId });
    });
  });
  if (itemRows.length === 0) return [];
  const { data, error } = await supabase.from("week_plan_items").insert(itemRows).select("id, list_type, recipe_id");
  if (error) throw error;
  return data;
}

// ---------------------------------------------------- week plan sharing ---
// Household-to-household equivalent of the recipe-sharing section above
// (supabase/migrations/0018-0021_week_plan_*.sql). Simpler in one way than
// recipe sharing despite covering more tables: a plan/day share always
// implies full edit collaboration, so there's no editableByOthers toggle to
// persist here.

// Lazily creates the week_plan_days row for (householdId, weekStart, day)
// if it doesn't exist yet, and returns its id either way -- used only by
// the mutating calls below (creating a day share/link). Ordinary days that
// are never shared never get a row (see 0018's comment on why this stays
// purely additive to week_plan_meals).
async function ensureWeekPlanDay(householdId, weekStart, day) {
  const { data, error } = await supabase.from("week_plan_days").insert({ household_id: householdId, week_start: weekStart, day }).select("id").single();
  if (!error) return data.id;
  if (error.code !== "23505") throw error;
  const { data: existing, error: selectError } = await supabase
    .from("week_plan_days")
    .select("id")
    .eq("household_id", householdId)
    .eq("week_start", weekStart)
    .eq("day", day)
    .single();
  if (selectError) throw selectError;
  return existing.id;
}

// Read-only counterpart for listing calls -- unlike ensureWeekPlanDay, never
// creates the row, since "no day row yet" just means "never shared," which
// should list as empty rather than manufacture a row as a side effect of
// looking.
async function findWeekPlanDayId(householdId, weekStart, day) {
  const { data, error } = await supabase.from("week_plan_days").select("id").eq("household_id", householdId).eq("week_start", weekStart).eq("day", day).maybeSingle();
  if (error) throw error;
  return data?.id || null;
}

export async function shareWeekPlan(householdId, weekStart, email, sections) {
  const { data: sharedWithHouseholdId, error: lookupError } = await supabase.rpc("find_household_id_by_member_email", { target_email: email });
  if (lookupError) throw lookupError;
  if (!sharedWithHouseholdId) throw new Error(`No account found for ${email}`);
  const { error } = await supabase.from("week_plan_shares").insert({
    household_id: householdId,
    week_start: weekStart,
    shared_with_household_id: sharedWithHouseholdId,
    include_snacks: !!sections?.includeSnacks,
    include_preserves: !!sections?.includePreserves,
    include_medicinal: !!sections?.includeMedicinal,
  });
  if (error) {
    // Postgres unique_violation -- the (household_id, week_start,
    // shared_with_household_id) constraint already blocks a duplicate
    // share; check_violation is the shared_with_household_id <>
    // household_id guard (sharing with a member of your own household).
    if (error.code === "23505") throw new Error("This week is already shared with that household.");
    if (error.code === "23514") throw new Error("That email belongs to your own household.");
    throw error;
  }
}

export async function listWeekPlanShares(householdId, weekStart) {
  const { data, error } = await supabase.rpc("list_week_plan_shares", { target_household_id: householdId, target_week_start: weekStart });
  if (error) throw error;
  return data || [];
}

export async function revokeWeekPlanShare(shareId) {
  const { error } = await supabase.from("week_plan_shares").delete().eq("id", shareId);
  if (error) throw error;
}

// The current household's own "who shared this with us" fact -- mirrors
// getRecipeSharedBy's scoping (get_week_plan_shared_by only ever reveals
// the fact to the recipient side of the share).
export async function getWeekPlanSharedBy(householdId, weekStart) {
  const { data, error } = await supabase.rpc("get_week_plan_shared_by", { target_household_id: householdId, target_week_start: weekStart });
  if (error) throw error;
  return data || "";
}

export async function shareWeekPlanDay(householdId, weekStart, day, email) {
  const weekPlanDayId = await ensureWeekPlanDay(householdId, weekStart, day);
  const { data: sharedWithHouseholdId, error: lookupError } = await supabase.rpc("find_household_id_by_member_email", { target_email: email });
  if (lookupError) throw lookupError;
  if (!sharedWithHouseholdId) throw new Error(`No account found for ${email}`);
  const { error } = await supabase.from("week_plan_day_shares").insert({
    week_plan_day_id: weekPlanDayId,
    household_id: householdId,
    shared_with_household_id: sharedWithHouseholdId,
  });
  if (error) {
    if (error.code === "23505") throw new Error("This day is already shared with that household.");
    if (error.code === "23514") throw new Error("That email belongs to your own household.");
    throw error;
  }
}

export async function listWeekPlanDayShares(householdId, weekStart, day) {
  const { data, error } = await supabase.rpc("list_week_plan_day_shares", { target_household_id: householdId, target_week_start: weekStart, target_day: day });
  if (error) throw error;
  return data || [];
}

export async function revokeWeekPlanDayShare(shareId) {
  const { error } = await supabase.from("week_plan_day_shares").delete().eq("id", shareId);
  if (error) throw error;
}

export async function getWeekPlanDaySharedBy(weekPlanDayId) {
  const { data, error } = await supabase.rpc("get_week_plan_day_shared_by", { target_week_plan_day_id: weekPlanDayId });
  if (error) throw error;
  return data || "";
}

// --------------------------------------------------- week plan share links ---
// Read-only bearer-token links, separate from the by-email shares above --
// same relationship recipe_share_links has to recipe_shares (see
// 0020_week_plan_share_links.sql's comment).
export async function listWeekPlanShareLinks(householdId, weekStart) {
  const { data, error } = await supabase
    .from("week_plan_share_links")
    .select("id, token, include_snacks, include_preserves, include_medicinal, created_at")
    .eq("household_id", householdId)
    .eq("week_start", weekStart)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createWeekPlanShareLink(householdId, weekStart, sections) {
  const { data, error } = await supabase
    .from("week_plan_share_links")
    .insert({
      household_id: householdId,
      week_start: weekStart,
      include_snacks: !!sections?.includeSnacks,
      include_preserves: !!sections?.includePreserves,
      include_medicinal: !!sections?.includeMedicinal,
    })
    .select("id, token, include_snacks, include_preserves, include_medicinal, created_at")
    .single();
  if (error) throw error;
  return data;
}

export async function revokeWeekPlanShareLink(linkId) {
  const { error } = await supabase.from("week_plan_share_links").delete().eq("id", linkId);
  if (error) throw error;
}

// The one call a signed-out visitor makes for a plan link -- see
// get_week_plan_by_share_token's comment for why this is safe to expose to
// `anon`. Returns null for an invalid/revoked token rather than throwing.
export async function getWeekPlanByShareToken(token) {
  const { data, error } = await supabase.rpc("get_week_plan_by_share_token", { target_token: token });
  if (error) throw error;
  return data || null;
}

export async function listWeekPlanDayShareLinks(householdId, weekStart, day) {
  const weekPlanDayId = await findWeekPlanDayId(householdId, weekStart, day);
  if (!weekPlanDayId) return [];
  const { data, error } = await supabase
    .from("week_plan_day_share_links")
    .select("id, token, created_at")
    .eq("week_plan_day_id", weekPlanDayId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createWeekPlanDayShareLink(householdId, weekStart, day) {
  const weekPlanDayId = await ensureWeekPlanDay(householdId, weekStart, day);
  const { data, error } = await supabase.from("week_plan_day_share_links").insert({ week_plan_day_id: weekPlanDayId }).select("id, token, created_at").single();
  if (error) throw error;
  return data;
}

export async function revokeWeekPlanDayShareLink(linkId) {
  const { error } = await supabase.from("week_plan_day_share_links").delete().eq("id", linkId);
  if (error) throw error;
}

export async function getWeekPlanDayByShareToken(token) {
  const { data, error } = await supabase.rpc("get_week_plan_day_by_share_token", { target_token: token });
  if (error) throw error;
  return data || null;
}

// ------------------------------------------------------- shared with us ---
// The "Shared with us" page's data: every plan-level and day-level share
// where the signed-in household is the RECIPIENT, not the owner (the
// mirror image of listWeekPlanShares/listWeekPlanDayShares above, which are
// owner-side). Both queries work under 0018's already-applied RLS
// (week_plan_shares_select/week_plan_day_shares_select allow either side of
// a share to see the row) -- no RPC needed for the listing itself, only for
// the attribution (getWeekPlanSharedBy/getWeekPlanDaySharedBy) and the
// preview content (below), since those need to reach across households.
export async function loadSharedPlansWithUs(householdId) {
  const { data, error } = await supabase
    .from("week_plan_shares")
    .select("id, household_id, week_start, include_snacks, include_preserves, include_medicinal, created_at")
    .eq("shared_with_household_id", householdId)
    .order("week_start", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function loadSharedDaysWithUs(householdId) {
  const { data, error } = await supabase
    .from("week_plan_day_shares")
    .select("id, week_plan_day_id, created_at, week_plan_days(household_id, week_start, day)")
    .eq("shared_with_household_id", householdId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

// Read-only preview content for a shared week/day -- see
// 0022_week_plan_for_household.sql's comment for why these work today even
// though real collaborative editing of another household's plan still
// needs 0019 to ship first.
export async function getWeekPlanForHousehold(householdId, weekStart) {
  const { data, error } = await supabase.rpc("get_week_plan_for_household", { target_household_id: householdId, target_week_start: weekStart });
  if (error) throw error;
  return data || null;
}

export async function getWeekPlanDayForHousehold(householdId, weekStart, day) {
  const { data, error } = await supabase.rpc("get_week_plan_day_for_household", { target_household_id: householdId, target_week_start: weekStart, target_day: day });
  if (error) throw error;
  return data || null;
}

// ------------------------------------------------- editing a shared plan ---
// Piece C of the collaborative-editing follow-up: real editing of a
// plan/day another household shared with you, via
// 0025_week_plan_write_for_household.sql's RPCs -- a parallel path
// alongside piece B's plain-table saveWeekPlanMeal/etc. above, which stay
// correct for a household editing its OWN week but would be rejected by
// the old household-only RLS for anyone else's. See that migration's
// header comment for why these work today regardless of
// 0019_week_plan_visibility_lockdown.sql's status.

// Reshapes get_week_plan_for_household's flat {meals, items} jsonb into
// the same days/snacks/preserves/medicinal/leftovers shape loadWeekPlans
// already produces for one's own weeks, so the shared-plan editor can
// reuse the exact same rendering/mutation conventions as PlanPage.jsx.
export async function loadWeekPlanForHouseholdEditable(householdId, weekStart) {
  const plan = await getWeekPlanForHousehold(householdId, weekStart);
  if (!plan) return null;
  const days = {};
  const leftovers = {};
  const leftoverSources = {};
  (plan.meals || []).forEach((m) => {
    const key = `${m.day}|${m.mealSlot}|${m.dishType}`;
    days[m.day] = days[m.day] || {};
    days[m.day][m.mealSlot] = days[m.day][m.mealSlot] || {};
    days[m.day][m.mealSlot][m.dishType] = m.isDiningOut ? DINING_OUT : m.recipeId || "";
    if (m.isLeftover) leftovers[key] = true;
    if (m.isLeftover && m.source) leftoverSources[key] = { ...m.source, scalesSource: m.source.scalesSource !== false };
  });
  const byType = { snack: [], preserves: [], medicinal: [] };
  (plan.items || []).forEach((it) => {
    if (byType[it.listType]) byType[it.listType].push({ id: it.id, recipeId: it.recipeId });
  });
  return {
    title: plan.title || "",
    days,
    snacks: byType.snack,
    preserves: byType.preserves,
    medicinal: byType.medicinal,
    servings: plan.servings,
    dayBudgets: plan.dayBudgets || {},
    leftovers,
    leftoverSources,
  };
}

// Same reshaping, day-scoped -- just the slot/dishType map and that day's
// leftover flags, no title/servings/items (day shares never carry those).
export async function loadWeekPlanDayForHouseholdEditable(householdId, weekStart, day) {
  const dayPlan = await getWeekPlanDayForHousehold(householdId, weekStart, day);
  if (!dayPlan) return null;
  const slots = {};
  const leftovers = {};
  (dayPlan.meals || []).forEach((m) => {
    slots[m.mealSlot] = slots[m.mealSlot] || {};
    slots[m.mealSlot][m.dishType] = m.isDiningOut ? DINING_OUT : m.recipeId || "";
    if (m.isLeftover) leftovers[`${m.mealSlot}|${m.dishType}`] = true;
  });
  return { slots, leftovers };
}

export async function saveWeekPlanMealForHousehold(householdId, weekStart, day, mealSlot, dishType, recipeId, isLeftover) {
  const isDiningOut = recipeId === DINING_OUT;
  const { error } = await supabase.rpc("upsert_week_plan_meal_for_household", {
    target_household_id: householdId,
    target_week_start: weekStart,
    target_day: day,
    target_meal_slot: mealSlot,
    target_dish_type: dishType,
    target_recipe_id: isDiningOut ? null : recipeId || null,
    target_is_leftover: !!isLeftover,
    target_is_dining_out: isDiningOut,
  });
  if (error) throw error;
}

export async function deleteWeekPlanMealRowForHousehold(householdId, weekStart, day, mealSlot, dishType) {
  const { error } = await supabase.rpc("delete_week_plan_meal_for_household", {
    target_household_id: householdId,
    target_week_start: weekStart,
    target_day: day,
    target_meal_slot: mealSlot,
    target_dish_type: dishType,
  });
  if (error) throw error;
}

export async function addWeekPlanItemForHousehold(householdId, weekStart, listType, recipeId) {
  const { data, error } = await supabase.rpc("add_week_plan_item_for_household", {
    target_household_id: householdId,
    target_week_start: weekStart,
    target_list_type: listType,
    target_recipe_id: recipeId,
  });
  if (error) throw error;
  return data;
}

export async function removeWeekPlanItemForHousehold(itemId) {
  const { error } = await supabase.rpc("remove_week_plan_item_for_household", { target_item_id: itemId });
  if (error) throw error;
}

export async function updateWeekPlanMetaForHousehold(householdId, weekStart, { title, servings, dayBudgets }) {
  const { error } = await supabase.rpc("update_week_plan_meta_for_household", {
    target_household_id: householdId,
    target_week_start: weekStart,
    target_title: title || "",
    target_servings: servings || 4,
    target_day_budgets: dayBudgets || {},
  });
  if (error) throw error;
}

// ------------------------------------------------- meal plan templates ---
// Public plan templates -- the last piece of the meal-plan-sharing design.
// Unlike everything above, this needs no RPCs at all: a template has
// exactly one writer (its owning household), so plain RLS-gated table
// operations are sufficient, and 0026's publish-time trigger guarantees
// every recipe in a PUBLIC template is itself already public -- so
// rendering one never needs a bypass read either, `recipes` (loaded once
// per session via the normal can_view_recipe-gated RLS) already has
// everything a public template could possibly reference.

// weekData -> template snapshot: strips the {id, recipeId} item shape down
// to bare recipe ids (a template has no live rows to individually address)
// and drops leftovers (not meaningful outside a specific dated week).
export function weekDataToTemplateSnapshot(weekData) {
  return {
    days: weekData.days || {},
    snacks: (weekData.snacks || []).map((entry) => entry.recipeId),
    preserves: (weekData.preserves || []).map((entry) => entry.recipeId),
    medicinal: (weekData.medicinal || []).map((entry) => entry.recipeId),
    dayBudgets: weekData.dayBudgets || {},
    servings: weekData.servings || 4,
  };
}

// The reverse, for "Use as template": bare recipe ids -> {recipeId}
// entries shaped for replaceWeekPlanWeek (piece B), which fans them out
// into fresh week_plan_items rows with real ids -- a template's ids are
// never reused directly, same as copying from a past week already works.
export function templateSnapshotToWeekData(template) {
  return {
    title: template.title || "",
    days: template.days || {},
    snacks: (template.snacks || []).map((recipeId) => ({ recipeId })),
    preserves: (template.preserves || []).map((recipeId) => ({ recipeId })),
    medicinal: (template.medicinal || []).map((recipeId) => ({ recipeId })),
    dayBudgets: template.dayBudgets || {},
    servings: template.servings || 4,
    leftovers: {},
  };
}

export async function loadMyMealPlanTemplates(householdId) {
  const { data, error } = await supabase.from("meal_plan_templates").select("*").eq("source_household_id", householdId).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function loadPublicMealPlanTemplates() {
  const { data, error } = await supabase.from("meal_plan_templates").select("*").eq("is_public", true).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function createMealPlanTemplate(householdId, snapshot, title, isPublic) {
  const { data, error } = await supabase
    .from("meal_plan_templates")
    .insert({
      source_household_id: householdId,
      title,
      days: snapshot.days,
      snacks: snapshot.snacks,
      preserves: snapshot.preserves,
      medicinal: snapshot.medicinal,
      day_budgets: snapshot.dayBudgets,
      servings: snapshot.servings,
      is_public: !!isPublic,
    })
    .select()
    .single();
  if (error) {
    // The publish-time trigger's raised exception surfaces as a plain
    // Postgres error (not a distinct code) -- pass its message through
    // as-is rather than a generic fallback, since it already names what's
    // wrong (unpublished recipes) in a way the UI should just show
    // directly.
    throw new Error(error.message || "Couldn't publish this template.");
  }
  return data;
}

export async function updateMealPlanTemplate(id, { title, isPublic }) {
  const patch = {};
  if (title !== undefined) patch.title = title;
  if (isPublic !== undefined) patch.is_public = !!isPublic;
  const { error } = await supabase.from("meal_plan_templates").update(patch).eq("id", id);
  if (error) throw new Error(error.message || "Couldn't update this template.");
}

export async function deleteMealPlanTemplate(id) {
  const { error } = await supabase.from("meal_plan_templates").delete().eq("id", id);
  if (error) throw error;
}

// ----------------------------------------------------------- plan defaults ---
// Diner ids this tab last loaded or saved for a household (only ones the
// viewer may manage). savePlanDefaults deletes exactly the ids that were in
// this set but are no longer in the list -- NOT "everything in the database
// that isn't in the list" -- because a second member may have added or
// linked a diner since this tab loaded, and diffing against the database
// would delete their work.
const knownDinerIds = new Map();

export async function loadPlanDefaults(householdId, userId) {
  const { data: row } = await supabase.from("plan_defaults").select("*").eq("household_id", householdId).maybeSingle();
  const budgetRows = await selectAll(() => supabase.from("meal_time_budgets").select("*").eq("household_id", householdId).order("day", { ascending: true }).order("meal_slot", { ascending: true }));
  // household_diners holds names and the optional account link, visible to
  // every member. The four numbers live in diner_targets, whose RLS only
  // returns rows for diners the viewer may manage (see 0038) -- so a
  // diner linked to someone else simply has no row here.
  const dinerRows = await selectAll(() => supabase.from("household_diners").select("id, name, user_id, created_at").eq("household_id", householdId).order("created_at", { ascending: true }).order("id", { ascending: true }), { uniqueKey: "id" });
  const targetRows = await selectAll(() => supabase.from("diner_targets").select("*").order("diner_id", { ascending: true }), { uniqueKey: "diner_id" });
  const targetsByDiner = new Map(targetRows.map((t) => [t.diner_id, t]));

  const mealTimeBudgets = {};
  (budgetRows || []).forEach((b) => {
    mealTimeBudgets[b.day] = mealTimeBudgets[b.day] || {};
    mealTimeBudgets[b.day][b.meal_slot] = b.minutes;
  });

  const householdMembers = (dinerRows || []).map((m) => {
    const t = targetsByDiner.get(m.id);
    return {
      id: m.id,
      name: m.name,
      // The account this person is linked to, or null for an unlinked diner
      // (managed by every member).
      userId: m.user_id || null,
      // Linked to someone else: name only, numbers are private to them.
      targetsHidden: !!m.user_id && m.user_id !== userId,
      // numeric columns -- see the matching comment in loadRecipes.
      dailyCalorieTarget: Number(t?.daily_calorie_target) || 0,
      dailyProteinTarget: Number(t?.daily_protein_target) || 0,
      dailyCarbsTarget: Number(t?.daily_carbs_target) || 0,
      dailyFatTarget: Number(t?.daily_fat_target) || 0,
    };
  });
  knownDinerIds.set(householdId, new Set(householdMembers.filter((m) => !m.targetsHidden).map((m) => m.id)));

  return {
    defaultServings: row?.default_servings ?? 4,
    weekStartDay: row?.week_start_day ?? "Monday",
    mealTimeBudgets,
    householdMembers,
  };
}

export function savePlanDefaults(householdId, planDefaults, userId) {
  return serialized(`planDefaults:${householdId}`, () => savePlanDefaultsInner(householdId, planDefaults, userId));
}

async function savePlanDefaultsInner(householdId, planDefaults, userId) {
  const { error } = await supabase.from("plan_defaults").upsert({
    household_id: householdId,
    default_servings: planDefaults.defaultServings || 4,
    week_start_day: planDefaults.weekStartDay || "Monday",
  });
  if (error) throw error;

  const { error: deleteBudgetsError } = await supabase.from("meal_time_budgets").delete().eq("household_id", householdId);
  if (deleteBudgetsError) throw deleteBudgetsError;
  const budgetRows = [];
  Object.entries(planDefaults.mealTimeBudgets || {}).forEach(([day, slots]) => {
    Object.entries(slots || {}).forEach(([mealSlot, minutes]) => {
      if (minutes > 0) budgetRows.push({ household_id: householdId, day, meal_slot: mealSlot, minutes });
    });
  });
  if (budgetRows.length > 0) {
    // meal_time_budgets has a natural composite key (household_id, day,
    // meal_slot) -- upsert for the same reason as week_plan_meals above.
    const { error: budgetError } = await supabase.from("meal_time_budgets").upsert(budgetRows);
    if (budgetError) throw budgetError;
  }

  // Diners are saved per person, not delete-all-and-reinsert: a linked
  // diner's row belongs to its owner, and RLS (0038) refuses writes to
  // anyone else's. Rows linked to another account (targetsHidden) are never
  // written at all -- this tab only ever saw their name.
  const manageable = dedupeById((planDefaults.householdMembers || []).filter((m) => !m.targetsHidden));
  const currentIds = new Set(manageable.map((m) => m.id));
  const removedIds = [...(knownDinerIds.get(householdId) || [])].filter((id) => !currentIds.has(id));
  if (removedIds.length > 0) {
    // diner_targets rows go with them (on delete cascade).
    const { error: deleteDinersError } = await supabase.from("household_diners").delete().eq("household_id", householdId).in("id", removedIds);
    if (deleteDinersError) throw deleteDinersError;
  }

  if (manageable.length > 0) {
    const dinerRows = manageable.map((m) => ({
      id: m.id,
      household_id: householdId,
      name: m.name || "",
      // Only ever null or the caller's own id -- RLS rejects anything else.
      user_id: m.userId && m.userId === userId ? userId : null,
    }));
    const { error: dinerError } = await supabase.from("household_diners").upsert(dinerRows);
    if (dinerError) throw dinerError;

    const targetRows = manageable.map((m) => ({
      diner_id: m.id,
      daily_calorie_target: Number(m.dailyCalorieTarget) || 0,
      daily_protein_target: Number(m.dailyProteinTarget) || 0,
      daily_carbs_target: Number(m.dailyCarbsTarget) || 0,
      daily_fat_target: Number(m.dailyFatTarget) || 0,
      updated_at: new Date().toISOString(),
    }));
    const { error: targetError } = await supabase.from("diner_targets").upsert(targetRows);
    if (targetError) throw targetError;
  }
  knownDinerIds.set(householdId, currentIds);
}

// ----------------------------------------------------------- inventory ---
// Slice I1 (docs/2026-09-20-inventory-design.md, Schemas/inventory-schema.md):
// food on hand for one plan week. Loaded once for the whole household and
// grouped by week_start, the same shape/convention as loadWeekPlans's
// plansByWeek -- not a per-week fetch on every week-switch, which would add
// a round trip to something users flip through quickly. Writes are simple
// per-row CRUD, not a load/save-whole-collection pair: the page has explicit
// add/edit/delete actions, so there's no "diff against what was there
// before" step to do. `quantity` arrives here already parsed to a number
// (parseQuantity happens in the UI layer, same boundary as everywhere else
// numeric input is typed as a string); category/unit/note are ordinary text.
export async function loadInventoryItems(householdId) {
  // Ordered by id (the primary key) so a page beyond the first is a total
  // order -- see selectAll's comment on why a non-unique ORDER BY silently
  // drops rows.
  const rows = await selectAll(() =>
    supabase.from("inventory_items").select("*").eq("household_id", householdId).order("id", { ascending: true })
  , { uniqueKey: "id" });
  const byWeek = {};
  (rows || []).forEach((r) => {
    (byWeek[r.week_start] = byWeek[r.week_start] || []).push({
      id: r.id,
      name: r.name,
      quantity: Number(r.quantity) || 0,
      unit: r.unit || "",
      category: r.category || "other",
      note: r.note || "",
    });
  });
  return byWeek;
}

export async function addInventoryItem(householdId, weekStart, item) {
  const { data, error } = await supabase
    .from("inventory_items")
    .insert({
      household_id: householdId,
      week_start: weekStart,
      name: item.name.trim(),
      quantity: Number(item.quantity) || 0,
      unit: (item.unit || "").trim(),
      category: item.category || "other",
      note: (item.note || "").trim(),
    })
    .select()
    .single();
  if (error) throw error;
  return { id: data.id, name: data.name, quantity: Number(data.quantity) || 0, unit: data.unit || "", category: data.category || "other", note: data.note || "" };
}

export async function updateInventoryItem(id, item) {
  const { error } = await supabase
    .from("inventory_items")
    .update({
      name: item.name.trim(),
      quantity: Number(item.quantity) || 0,
      unit: (item.unit || "").trim(),
      category: item.category || "other",
      note: (item.note || "").trim(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function deleteInventoryItem(id) {
  const { error } = await supabase.from("inventory_items").delete().eq("id", id);
  if (error) throw error;
}

// Purveyor slice P3: which boxes a household has already pulled into
// inventory, so PurveyorsPage can show "added" instead of risking a second
// (duplicate) insert on a repeat click. Ordered by id (the primary key), the
// same total-order shape as loadInventoryItems just above.
export async function loadInventoryBoxIds(householdId) {
  const rows = await selectAll(() =>
    supabase.from("inventory_items").select("box_id").eq("household_id", householdId).order("id", { ascending: true })
  , { uniqueKey: "id" });
  return new Set(rows.map((r) => r.box_id).filter(Boolean));
}

// The explicit "add this box to my inventory" action (P3). One bulk insert
// for the whole box, not a loop of single-row addInventoryItem calls -- a
// box's items should land together or not at all, not partially if one
// request fails. Name resolution mirrors PurveyorDetail's own label logic
// (snapshotName, frozen at Delivered, wins over a live product lookup, which
// wins over a one-off item's own name); category comes from the product's
// groceryCategory when there is one to look up.
export async function addBoxToInventory(householdId, purveyor, box, weekStart) {
  const rows = box.items.map((it) => {
    const product = it.productId ? purveyor.products.find((pr) => pr.id === it.productId) : null;
    return {
      household_id: householdId,
      week_start: weekStart,
      name: it.snapshotName || product?.name || it.oneOffName || "item",
      quantity: Number(it.quantity) || 0,
      unit: it.unit || "",
      category: product?.groceryCategory || "other",
      note: box.name ? `From ${purveyor.name} — ${box.name}` : `From ${purveyor.name}`,
      source: "purveyor",
      purveyor_id: purveyor.id,
      box_id: box.id,
    };
  });
  const { error } = await supabase.from("inventory_items").insert(rows);
  if (error) throw error;
}

// ---------------------------------------------------- purveyor accounts ---
// Purveyor slice P4 (docs/2026-09-27-purveyor-design.md): a purveyor login,
// parallel to a household account, not part of it -- so none of this takes
// a householdId. Membership/invite reads and writes go through
// security-definer RPCs (0050), the same shape as the household-membership
// functions above; box authoring below is direct table access, RLS-scoped
// to the caller's own purveyor_members rows.
export async function loadMyPurveyorMemberships() {
  const { data, error } = await supabase.rpc("my_purveyor_memberships");
  if (error) throw error;
  return (data || []).map((r) => ({ purveyorId: r.purveyor_id, purveyorName: r.purveyor_name }));
}

export async function loadMyPendingPurveyorInvites() {
  const { data, error } = await supabase.rpc("my_pending_purveyor_invites");
  if (error) throw error;
  return (data || []).map((r) => ({ id: r.id, purveyorId: r.purveyor_id, purveyorName: r.purveyor_name, createdAt: r.created_at, expiresAt: r.expires_at }));
}

export async function acceptPurveyorInvite(inviteId) {
  const { data, error } = await supabase.rpc("accept_purveyor_invite", { target_invite_id: inviteId });
  if (error) throw error;
  return data;
}

export async function declinePurveyorInvite(inviteId) {
  const { error } = await supabase.rpc("decline_purveyor_invite", { target_invite_id: inviteId });
  if (error) throw error;
}

// ------------------------------------------------------------- box authoring ---
// Planned/Confirmed boxes are ordinary RLS-scoped table writes (0050); the
// Confirmed -> Delivered transition is not -- see markBoxDelivered.
export async function createBox(purveyorId, { name = "", deliveryDate = null } = {}) {
  const { data, error } = await supabase
    .from("boxes")
    .insert({ purveyor_id: purveyorId, name: name.trim(), delivery_date: deliveryDate || null })
    .select()
    .single();
  if (error) throw error;
  return { id: data.id, name: data.name, deliveryDate: data.delivery_date, status: data.status };
}

export async function updateBox(boxId, { name, deliveryDate }) {
  const { error } = await supabase.from("boxes").update({ name: name.trim(), delivery_date: deliveryDate || null }).eq("id", boxId);
  if (error) throw error;
}

// Planned <-> Confirmed only -- a plain update can never reach 'Delivered'
// (the boxes_member_write policy's WITH CHECK blocks it); that transition is
// only ever markBoxDelivered, below.
export async function setBoxStatus(boxId, status) {
  const { error } = await supabase.from("boxes").update({ status }).eq("id", boxId);
  if (error) throw error;
}

export async function deleteBox(boxId) {
  const { error } = await supabase.from("boxes").delete().eq("id", boxId);
  if (error) throw error;
}

export async function addBoxItem(boxId, { productId = null, oneOffName = "", quantity = 0, unit = "" } = {}) {
  const { data, error } = await supabase
    .from("box_items")
    .insert({ box_id: boxId, product_id: productId || null, one_off_name: oneOffName.trim(), quantity: Number(quantity) || 0, unit: unit.trim() })
    .select()
    .single();
  if (error) throw error;
  return { id: data.id, productId: data.product_id, oneOffName: data.one_off_name, quantity: Number(data.quantity) || 0, unit: data.unit, snapshotName: data.snapshot_name };
}

export async function updateBoxItem(itemId, { productId = null, oneOffName = "", quantity = 0, unit = "" }) {
  const { error } = await supabase
    .from("box_items")
    .update({ product_id: productId || null, one_off_name: oneOffName.trim(), quantity: Number(quantity) || 0, unit: unit.trim() })
    .eq("id", itemId);
  if (error) throw error;
}

export async function deleteBoxItem(itemId) {
  const { error } = await supabase.from("box_items").delete().eq("id", itemId);
  if (error) throw error;
}

// box-schema.md's "Clone-a-box is the intended way to build a new box
// quickly": same items (productId/oneOffName/quantity/unit), fresh id,
// status reset to Planned, name/deliveryDate left blank for the user to set
// -- not copied. A UI behavior, not a schema field, so this is just two
// ordinary writes, no dedicated RPC.
export async function cloneBox(purveyorId, sourceBox) {
  const box = await createBox(purveyorId);
  if (sourceBox.items.length > 0) {
    const rows = sourceBox.items.map((it) => ({ box_id: box.id, product_id: it.productId || null, one_off_name: it.oneOffName || "", quantity: it.quantity || 0, unit: it.unit || "" }));
    const { error } = await supabase.from("box_items").insert(rows);
    if (error) throw error;
  }
  return box;
}

// The one-way Confirmed -> Delivered door (box-schema.md's locking rule):
// snapshotName gets written server-side, atomically with the status flip, by
// the security-definer RPC -- never by the client directly (see 0050).
export async function markBoxDelivered(boxId) {
  const { error } = await supabase.rpc("mark_box_delivered", { target_box_id: boxId });
  if (error) throw error;
}

// ---------------------------------------------------------------- catalog ---
// A purveyor's products, edited by its own members (0052, feedback #58).
// Deleting one that an open box still uses keeps the item's name there: a
// trigger copies it into one_off_name before the product goes.
export function productRow(product) {
  const name = (product.name || "").trim();
  if (!name) throw new Error("A product needs a name");
  const quantity = Number(product.quantity);
  return {
    name,
    description: (product.description || "").trim(),
    category: (product.category || "").trim(),
    grocery_category: product.groceryCategory || "other",
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 0,
    unit: (product.unit || "").trim(),
    availability: product.availability || "available",
    start_date: product.startDate || null,
    end_date: product.endDate || null,
  };
}

const productFromRow = (r) => ({
  id: r.id,
  name: r.name || "",
  description: r.description || "",
  category: r.category || "",
  groceryCategory: r.grocery_category || "other",
  quantity: Number(r.quantity) || 0,
  unit: r.unit || "",
  availability: r.availability || "available",
  startDate: r.start_date || "",
  endDate: r.end_date || "",
});

export async function createProduct(purveyorId, product) {
  const { data, error } = await supabase.from("products").insert({ purveyor_id: purveyorId, ...productRow(product) }).select().single();
  if (error) throw error;
  return productFromRow(data);
}

export async function updateProduct(productId, product) {
  const { data, error } = await supabase
    .from("products")
    .update({ ...productRow(product), updated_at: new Date().toISOString() })
    .eq("id", productId)
    .select()
    .single();
  if (error) throw error;
  return productFromRow(data);
}

export async function deleteProduct(productId) {
  const { error } = await supabase.from("products").delete().eq("id", productId);
  if (error) throw error;
}

// ------------------------------------------------------------- app admin ---
// App admin role (0051, feedback #56). isAppAdmin only decides whether the
// Admin tab is shown; every write below is refused by RLS or the RPC's own
// is_app_admin() check for anyone else, whatever the client shows. The
// profile writes (updatePurveyor and the location/hours calls) are also open
// to a purveyor's own members since 0052 -- the Purveyor Portal's Profile
// tab uses them too; creating/deleting a purveyor and the contact RPCs stay
// admin-only.
export async function isAppAdmin() {
  const { data, error } = await supabase.rpc("is_app_admin");
  if (error) throw error;
  return data === true;
}

// The purveyors row for a profile in loadPurveyorDirectory()'s shape. Pure,
// so the trimming and required fields are tested directly. Throws before any
// write rather than letting a half-empty purveyor into the directory.
export function purveyorProfileRow(profile) {
  const name = (profile.name || "").trim();
  if (!name) throw new Error("A purveyor needs a name");
  if (!profile.purveyorTypes || profile.purveyorTypes.length === 0) throw new Error("Pick at least one type");
  const loc = profile.location || {};
  const contact = profile.contactInfo || {};
  const t = (v) => (v || "").trim();
  return {
    name,
    description: t(profile.description),
    is_verified: !!profile.isVerified,
    address: t(loc.address),
    city: t(loc.city),
    state: t(loc.state),
    zip: t(loc.zip),
    phone: t(contact.phone),
    email: t(contact.email).toLowerCase(),
    website: t(contact.website),
    social_url: t(contact.socialUrl),
  };
}

// purveyor_types is a set keyed by (purveyor_id, purveyor_type): add what's
// new, remove what's gone, leave the rest alone.
async function syncPurveyorTypes(purveyorId, previousTypes, nextTypes) {
  const added = nextTypes.filter((t) => !previousTypes.includes(t));
  const removed = previousTypes.filter((t) => !nextTypes.includes(t));
  if (removed.length > 0) {
    const { error } = await supabase.from("purveyor_types").delete().eq("purveyor_id", purveyorId).in("purveyor_type", removed);
    if (error) throw error;
  }
  if (added.length > 0) {
    const { error } = await supabase.from("purveyor_types").insert(added.map((t) => ({ purveyor_id: purveyorId, purveyor_type: t })));
    if (error) throw error;
  }
}

export async function createPurveyor(profile) {
  const row = purveyorProfileRow(profile);
  const { data, error } = await supabase.from("purveyors").insert(row).select().single();
  if (error) throw error;
  await syncPurveyorTypes(data.id, [], profile.purveyorTypes);
  return data.id;
}

export async function updatePurveyor(purveyorId, profile, previousTypes) {
  const row = purveyorProfileRow(profile);
  const { error } = await supabase.from("purveyors").update({ ...row, updated_at: new Date().toISOString() }).eq("id", purveyorId);
  if (error) throw error;
  await syncPurveyorTypes(purveyorId, previousTypes, profile.purveyorTypes);
}

// Cascades to the purveyor's types, locations, hours, products, boxes,
// members and invites (0047/0050 foreign keys).
export async function deletePurveyor(purveyorId) {
  const { error } = await supabase.from("purveyors").delete().eq("id", purveyorId);
  if (error) throw error;
}

const locationRow = (loc) => ({ address: (loc.address || "").trim(), city: (loc.city || "").trim(), state: (loc.state || "").trim(), zip: (loc.zip || "").trim() });

export async function addPurveyorLocation(purveyorId, loc) {
  const { data, error } = await supabase.from("purveyor_locations").insert({ purveyor_id: purveyorId, ...locationRow(loc) }).select().single();
  if (error) throw error;
  return { id: data.id, address: data.address, city: data.city, state: data.state, zip: data.zip };
}

export async function updatePurveyorLocation(locationId, loc) {
  const { error } = await supabase.from("purveyor_locations").update(locationRow(loc)).eq("id", locationId);
  if (error) throw error;
}

// Hours tied to this location go with it (purveyor_hours.location_id is
// ON DELETE CASCADE).
export async function deletePurveyorLocation(locationId) {
  const { error } = await supabase.from("purveyor_locations").delete().eq("id", locationId);
  if (error) throw error;
}

const hoursRow = (h) => ({
  location_id: h.locationId || null,
  day_of_week: h.dayOfWeek,
  open_time: (h.openTime || "").trim(),
  close_time: (h.closeTime || "").trim(),
  season_start: h.seasonStart || null,
  season_end: h.seasonEnd || null,
});

export async function addPurveyorHours(purveyorId, h) {
  const { data, error } = await supabase.from("purveyor_hours").insert({ purveyor_id: purveyorId, ...hoursRow(h) }).select().single();
  if (error) throw error;
  return { id: data.id, locationId: data.location_id, dayOfWeek: data.day_of_week, openTime: data.open_time || "", closeTime: data.close_time || "", seasonStart: data.season_start || "", seasonEnd: data.season_end || "" };
}

export async function updatePurveyorHours(hoursId, h) {
  const { error } = await supabase.from("purveyor_hours").update(hoursRow(h)).eq("id", hoursId);
  if (error) throw error;
}

export async function deletePurveyorHours(hoursId) {
  const { error } = await supabase.from("purveyor_hours").delete().eq("id", hoursId);
  if (error) throw error;
}

// A purveyor's members and open invites, members first. An invite past its
// expiry is still listed (expired: true) so it can be revoked or re-sent.
export async function loadPurveyorContacts(purveyorId) {
  const { data, error } = await supabase.rpc("admin_list_purveyor_contacts", { target_purveyor_id: purveyorId });
  if (error) throw error;
  const now = Date.now();
  return (data || []).map((r) => ({
    kind: r.kind,
    inviteId: r.invite_id,
    userId: r.user_id,
    email: r.email,
    createdAt: r.created_at,
    expiresAt: r.expires_at,
    expired: r.kind === "invite" && !!r.expires_at && new Date(r.expires_at).getTime() <= now,
  }));
}

export async function invitePurveyorContact(purveyorId, email) {
  const { data, error } = await supabase.rpc("admin_create_purveyor_invite", { target_purveyor_id: purveyorId, target_email: email });
  if (error) throw error;
  return data;
}

export async function revokePurveyorInvite(inviteId) {
  const { error } = await supabase.rpc("admin_revoke_purveyor_invite", { target_invite_id: inviteId });
  if (error) throw error;
}

export async function removePurveyorMember(purveyorId, userId) {
  const { error } = await supabase.rpc("admin_remove_purveyor_member", { target_purveyor_id: purveyorId, target_user_id: userId });
  if (error) throw error;
}

// ------------------------------------------------------------- feedback ---
// One-way: nothing in the app ever reads feedback back, so there's no
// matching loadFeedback -- just a plain insert.
export async function submitFeedback(householdId, email, message, page) {
  const { error } = await supabase.from("feedback").insert({ household_id: householdId, email, message, page });
  if (error) throw error;
}

// ------------------------------------------------- household membership ---
// Invites, seats and the members list (see docs/2026-09-19-household-
// membership-design.md and migrations 0040-0042). Every write here goes
// through a security-definer RPC -- the client has no direct write access to
// household_accounts / households / household_invites -- and the RPCs raise
// plain-language errors ("Your household has no free seats", ...) that the UI
// shows as-is via err.message.
export async function loadHouseholdSummary() {
  const { data, error } = await supabase.rpc("my_household_summary");
  if (error) throw error;
  const row = (data || [])[0];
  return row
    ? { householdId: row.household_id, role: row.role, memberCount: row.member_count, pendingInviteCount: row.pending_invite_count, seatLimit: row.seat_limit }
    : null;
}

export async function loadHouseholdMembers() {
  const { data, error } = await supabase.rpc("list_household_members");
  if (error) throw error;
  return data || [];
}

export async function loadHouseholdInvites() {
  const { data, error } = await supabase.rpc("list_household_invites");
  if (error) throw error;
  return data || [];
}

export async function createHouseholdInvite(email) {
  const { data, error } = await supabase.rpc("create_household_invite", { target_email: email });
  if (error) throw error;
  return data;
}

export async function revokeHouseholdInvite(inviteId) {
  const { error } = await supabase.rpc("revoke_household_invite", { target_invite_id: inviteId });
  if (error) throw error;
}

export async function loadMyPendingHouseholdInvites() {
  const { data, error } = await supabase.rpc("my_pending_household_invites");
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    householdId: r.household_id,
    invitedByEmail: r.invited_by_email,
    expiresAt: r.expires_at,
    alreadyInHousehold: !!r.already_in_household,
  }));
}

export async function acceptHouseholdInvite(inviteId, shareRecipes) {
  const { data, error } = await supabase.rpc("accept_household_invite", { target_invite_id: inviteId, share_recipes: !!shareRecipes });
  if (error) throw error;
  return data;
}

export async function declineHouseholdInvite(inviteId) {
  const { error } = await supabase.rpc("decline_household_invite", { target_invite_id: inviteId });
  if (error) throw error;
}

export async function createOwnHousehold() {
  const { data, error } = await supabase.rpc("create_own_household");
  if (error) throw error;
  return data;
}

// The caller's own "let my household see my recipes" switch. The only column
// of household_accounts the client may write (a column-level grant, 0040).
export async function loadShareRecipesWithHousehold(userId, householdId) {
  const { data, error } = await supabase.from("household_accounts").select("share_recipes_with_household").eq("user_id", userId).eq("household_id", householdId).maybeSingle();
  if (error) throw error;
  return !!data?.share_recipes_with_household;
}

export async function setShareRecipesWithHousehold(userId, householdId, value) {
  const { error } = await supabase.from("household_accounts").update({ share_recipes_with_household: !!value }).eq("user_id", userId).eq("household_id", householdId);
  if (error) throw error;
}

// Leaving and removal (0043). `shareRecipeIds` is the optional pre-leave step:
// recipes the leaver owns that become explicit shares with everyone staying.
export async function leaveHousehold(shareRecipeIds = []) {
  const { error } = await supabase.rpc("leave_household", { share_recipe_ids: shareRecipeIds });
  if (error) throw error;
}

export async function removeHouseholdMember(targetUserId) {
  const { error } = await supabase.rpc("remove_household_member", { target_user_id: targetUserId });
  if (error) throw error;
}

// Emails the invitation (Edge Function send-household-invite, 0044). The
// invite already exists by the time this is called, so callers treat a failure
// here as "created, but not emailed" rather than as a failed invite. The
// function's plain-language messages ("Wait 5 minutes before resending", ...)
// travel in the response body -- same unwrapping as importRecipeFromUrl.
export async function sendHouseholdInviteEmail(inviteId) {
  const { error } = await supabase.functions.invoke("send-household-invite", { body: { inviteId } });
  if (error) {
    let message = "We couldn't send the email.";
    if (error.context && typeof error.context.json === "function") {
      try {
        const body = await error.context.json();
        message = body?.message || message;
      } catch {
        // Not JSON (e.g. the network dropped before the function ran).
      }
    }
    throw new Error(message);
  }
}

// ------------------------------------------------------- purveyor directory ---
// Purveyor slice P1 (docs/2026-09-27-purveyor-design.md, Schemas/purveyor-
// schema.md/product-schema.md/box-schema.md): the producer-side directory.
// Read-only in this slice -- no add/edit/delete here on purpose. The eight
// tables are global reference data, not household-scoped, so this loads
// everything in one call rather than being keyed by householdId the way
// loadWeekPlans etc. are. Expected to stay small for a while (a single-
// region pilot directory), so "load it all at once, assemble client-side"
// is the same choice already made for recipes.
// orderBy must be a TOTAL order (selectAll's rule, and the Sept 20 lesson
// behind it) -- a single column only works when that column is itself the
// table's primary key. purveyor_types and purveyor_venues have no surrogate
// id (their PK is the pair of columns given here), so both columns of the
// composite key are passed, in order -- and, since neither column alone is
// unique, `uniqueKey` is deliberately omitted for those two calls (a single
// non-unique column there would make selectAll's overlap safety net wrongly
// collapse distinct rows that merely share one column; loadWeekPlans's
// 4-column week_plan_meals load follows the same rule, no uniqueKey passed
// for a composite-only key). Every other table here has a real `id` PK.
async function loadPurveyorTable(table, orderBy = ["id"]) {
  return selectAll(
    () => orderBy.reduce((q, col) => q.order(col, { ascending: true }), supabase.from(table).select("*")),
    orderBy.length === 1 ? { uniqueKey: orderBy[0] } : {}
  );
}

export async function loadPurveyorDirectory() {
  const [purveyorRows, typeRows, locationRows, hoursRows, venueRows, productRows, boxRows, boxItemRows] = await Promise.all([
    loadPurveyorTable("purveyors"),
    loadPurveyorTable("purveyor_types", ["purveyor_id", "purveyor_type"]),
    loadPurveyorTable("purveyor_locations"),
    loadPurveyorTable("purveyor_hours"),
    loadPurveyorTable("purveyor_venues", ["vendor_id", "venue_id"]),
    loadPurveyorTable("products"),
    loadPurveyorTable("boxes"),
    loadPurveyorTable("box_items"),
  ]);

  const typesByPurveyor = groupBy(typeRows, "purveyor_id");
  const locationsByPurveyor = groupBy(locationRows, "purveyor_id");
  const hoursByPurveyor = groupBy(hoursRows, "purveyor_id");
  const productsByPurveyor = groupBy(productRows, "purveyor_id");
  const boxItemsByBox = groupBy(boxItemRows, "box_id");
  const vendorIdsByVenue = {};
  const venueIdsByVendor = {};
  venueRows.forEach((v) => {
    (vendorIdsByVenue[v.venue_id] = vendorIdsByVenue[v.venue_id] || []).push(v.vendor_id);
    (venueIdsByVendor[v.vendor_id] = venueIdsByVendor[v.vendor_id] || []).push(v.venue_id);
  });
  const boxesByPurveyor = groupBy(
    boxRows.map((b) => ({
      ...b,
      items: (boxItemsByBox[b.id] || []).map((it) => ({
        id: it.id,
        productId: it.product_id,
        oneOffName: it.one_off_name || "",
        quantity: Number(it.quantity) || 0,
        unit: it.unit || "",
        snapshotName: it.snapshot_name || "",
      })),
    })),
    "purveyor_id"
  );

  return purveyorRows.map((p) => ({
    id: p.id,
    name: p.name || "",
    description: p.description || "",
    isVerified: !!p.is_verified,
    location: { address: p.address || "", city: p.city || "", state: p.state || "", zip: p.zip || "" },
    contactInfo: { phone: p.phone || "", email: p.email || "", website: p.website || "", socialUrl: p.social_url || "" },
    purveyorTypes: (typesByPurveyor[p.id] || []).map((t) => t.purveyor_type),
    pickupLocations: (locationsByPurveyor[p.id] || []).map((l) => ({ id: l.id, address: l.address || "", city: l.city || "", state: l.state || "", zip: l.zip || "" })),
    hours: (hoursByPurveyor[p.id] || []).map((h) => ({
      id: h.id,
      locationId: h.location_id,
      dayOfWeek: h.day_of_week,
      openTime: h.open_time || "",
      closeTime: h.close_time || "",
      seasonStart: h.season_start || "",
      seasonEnd: h.season_end || "",
    })),
    vendorIds: vendorIdsByVenue[p.id] || [],
    venueIds: venueIdsByVendor[p.id] || [],
    products: (productsByPurveyor[p.id] || []).map((pr) => ({
      id: pr.id,
      name: pr.name || "",
      description: pr.description || "",
      category: pr.category || "",
      groceryCategory: pr.grocery_category || "other",
      quantity: Number(pr.quantity) || 0,
      unit: pr.unit || "",
      availability: pr.availability || "available",
      startDate: pr.start_date || "",
      endDate: pr.end_date || "",
    })),
    boxes: (boxesByPurveyor[p.id] || []).map((b) => ({
      id: b.id,
      name: b.name || "",
      deliveryDate: b.delivery_date || "",
      status: b.status || "Planned",
      items: b.items,
    })),
  }));
}

// ---------------------------------------------------- household sources ---
// Purveyor slice P2 (docs/2026-09-27-purveyor-design.md): "tag this purveyor
// as a source I use." household_sources has no surrogate id -- its primary
// key is (household_id, purveyor_id) -- so, per the lesson from P1 (see that
// slice's "A lesson from building P1"), the load orders by BOTH columns and
// deliberately omits `uniqueKey` (neither column alone is unique here; a
// single-column uniqueKey would make selectAll's overlap safety net wrongly
// collapse distinct rows that merely share one column). This function was
// written test-first, against tests/select-all-ordering.test.mjs's lopsided-
// fanout regression test, specifically so that lesson wasn't just noted, it
// was acted on before the bug had a chance to exist here too.
export async function loadHouseholdSources(householdId) {
  const rows = await selectAll(() =>
    supabase.from("household_sources").select("purveyor_id").eq("household_id", householdId).order("household_id", { ascending: true }).order("purveyor_id", { ascending: true })
  );
  return rows.map((r) => r.purveyor_id);
}

export async function addHouseholdSource(householdId, purveyorId) {
  const { error } = await supabase.from("household_sources").insert({ household_id: householdId, purveyor_id: purveyorId });
  if (error) throw error;
}

export async function removeHouseholdSource(householdId, purveyorId) {
  const { error } = await supabase.from("household_sources").delete().eq("household_id", householdId).eq("purveyor_id", purveyorId);
  if (error) throw error;
}

// ------------------------------------------------------ grocery list state ---
// Slice 6a (0055, docs/2026-10-04-groceries-design.md): the marks on a week's
// list and the household's added items. The list itself stays derived from
// the plan (buildGroceryList); groceryState.ts reads these against it.

export function groceryMarkFromRow(r) {
  return {
    weekStart: r.week_start,
    itemKey: r.item_key,
    mark: r.mark ?? null,
    amounts: Array.isArray(r.amounts) ? r.amounts : [],
    changedAt: new Date(r.changed_at).toISOString(),
  };
}

export function groceryExtraFromRow(r) {
  return {
    id: r.id,
    name: r.name,
    amount: r.amount || "",
    category: r.category || "other",
    firstWeek: r.first_week,
    lastWeek: r.last_week ?? null,
  };
}

// One week's marks, cleared ones included (they still decide which change
// is newest). Ordered by id, the primary key -- see selectAll.
export async function loadGroceryMarks(householdId, weekStart) {
  const rows = await selectAll(
    () => supabase.from("grocery_marks").select("*").eq("household_id", householdId).eq("week_start", weekStart).order("id", { ascending: true }),
    { uniqueKey: "id" }
  );
  return rows.map(groceryMarkFromRow);
}

// Sets or clears one mark (`mark: null`), keeping whichever change is
// newest (set_grocery_mark). Resolves to whether this change was kept; an
// older one losing to a newer is not an error.
export async function setGroceryMark(householdId, row) {
  const { data, error } = await supabase.rpc("set_grocery_mark", {
    p_household_id: householdId,
    p_week_start: row.weekStart,
    p_item_key: row.itemKey,
    p_mark: row.mark,
    p_amounts: row.amounts,
    p_changed_at: row.changedAt,
  });
  if (error) throw error;
  return data === true;
}

// Every added item the household has (pinned ones span many weeks, so
// they're loaded whole; groceryState.extrasForWeek picks a week's).
export async function loadGroceryExtras(householdId) {
  const rows = await selectAll(() => supabase.from("grocery_extras").select("*").eq("household_id", householdId).order("id", { ascending: true }), { uniqueKey: "id" });
  return rows.map(groceryExtraFromRow);
}

export async function addGroceryExtra(householdId, { name, amount = "", category = "other", firstWeek, lastWeek }) {
  const { data, error } = await supabase
    .from("grocery_extras")
    .insert({ household_id: householdId, name: name.trim(), amount: amount.trim(), category, first_week: firstWeek, last_week: lastWeek })
    .select("*")
    .single();
  if (error) throw error;
  return groceryExtraFromRow(data);
}

// Pin (lastWeek: null) or unpin (lastWeek: the week being viewed).
export async function setGroceryExtraLastWeek(id, lastWeek) {
  const { error } = await supabase.from("grocery_extras").update({ last_week: lastWeek }).eq("id", id);
  if (error) throw error;
}

export async function deleteGroceryExtra(id) {
  const { error } = await supabase.from("grocery_extras").delete().eq("id", id);
  if (error) throw error;
}

// ----------------------------------------------------------- recipe drafts ---
// Slice 7b (0056): a recipe being written or edited, saved as you type. One
// per person per key: "new" for a recipe not saved yet, else the id of the
// recipe being edited. `data` is { recipe, step }.

export async function loadRecipeDraft(userId, draftKey) {
  const { data, error } = await supabase.from("recipe_drafts").select("data, updated_at").eq("user_id", userId).eq("draft_key", draftKey).maybeSingle();
  if (error) throw error;
  return data ? { ...data.data, updatedAt: data.updated_at } : null;
}

export async function saveRecipeDraft(userId, draftKey, draft) {
  const { error } = await supabase
    .from("recipe_drafts")
    .upsert({ user_id: userId, draft_key: draftKey, data: draft, updated_at: new Date().toISOString() }, { onConflict: "user_id,draft_key" });
  if (error) throw error;
}

export async function deleteRecipeDraft(userId, draftKey) {
  const { error } = await supabase.from("recipe_drafts").delete().eq("user_id", userId).eq("draft_key", draftKey);
  if (error) throw error;
}

// The signed-in user's recipes that have their share link on (slice 8b): a
// plan link opens those recipes (0060), so the plan share sheet can say how
// many will open.
export async function loadMyRecipeLinkIds() {
  const { data, error } = await supabase.from("recipe_share_links").select("recipe_id");
  if (error) throw error;
  return new Set((data || []).map((r) => r.recipe_id));
}
